import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Apple,
  Check,
  ChevronLeft,
  Eye,
  FileText,
  Globe,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { api } from '../../lib/api';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';

// ── Types ──────────────────────────────────────────────────────────────────

interface PlatformVersion {
  platform: 'web' | 'ios' | 'android';
  currentVersion: string;
  minimumVersion: string;
  storeUrl?: string;
  updatedAt: string;
  updatedBy: string;
}

interface ReleaseNote {
  id: string;
  version: string;
  platforms: ('web' | 'ios' | 'android')[];
  title: string;
  body: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

type Platform = 'web' | 'ios' | 'android';

const PLATFORM_LABELS: Record<Platform, string> = { web: 'Web', ios: 'iOS', android: 'Android' };

/** Android robot head icon (lucide doesn't include brand icons) */
function AndroidIcon({ size = 24, className = '' }: { size?: number | string; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      <circle cx="9" cy="15" r="0.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="15" r="0.5" fill="currentColor" stroke="none" />
      <line x1="5" y1="5" x2="7.5" y2="8" />
      <line x1="19" y1="5" x2="16.5" y2="8" />
    </svg>
  );
}

const PLATFORM_ICONS: Record<Platform, React.ComponentType<{ size?: number | string; className?: string }>> = {
  web: Globe,
  ios: Apple,
  android: AndroidIcon,
};

const inputCls = `w-full h-10 px-3 rounded-lg border border-stone bg-white
  text-small text-midnight placeholder:text-slate/60
  focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors`;

// ── Semver validator (simple client-side check) ──────────────────────────

function isValidSemver(v: string): boolean {
  return /^\d+\.\d+\.\d+$/.test(v);
}

// ── Platform Version Card ─────────────────────────────────────────────────

function PlatformCard({ version, onSaved }: { version: PlatformVersion; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [currentVer, setCurrentVer] = useState(version.currentVersion);
  const [minVer, setMinVer] = useState(version.minimumVersion);
  const [storeUrl, setStoreUrl] = useState(version.storeUrl ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const Icon = PLATFORM_ICONS[version.platform];

  function startEdit() {
    setCurrentVer(version.currentVersion);
    setMinVer(version.minimumVersion);
    setStoreUrl(version.storeUrl ?? '');
    setError('');
    setEditing(true);
  }

  async function handleSave() {
    if (!isValidSemver(currentVer)) { setError('Current version must be valid semver (e.g. 1.2.3)'); return; }
    if (!isValidSemver(minVer)) { setError('Minimum version must be valid semver (e.g. 1.0.0)'); return; }

    setSaving(true);
    setError('');
    try {
      await api.patch(`/admin/app-versions/${version.platform}`, {
        currentVersion: currentVer,
        minimumVersion: minVer,
        storeUrl: storeUrl.trim() || undefined,
      });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="bg-white border border-stone rounded-xl shadow-brand-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon size={16} className="text-plum" />
            <p className="font-semibold text-small text-midnight">{PLATFORM_LABELS[version.platform]}</p>
          </div>
          <button type="button" onClick={() => setEditing(false)} className="text-slate hover:text-midnight">
            <X size={16} />
          </button>
        </div>
        {error && (
          <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30">
            <AlertCircle size={14} className="shrink-0 text-rose mt-0.5" />
            <p className="text-[12px] text-rose">{error}</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Current Version</label>
            <input type="text" value={currentVer} onChange={e => setCurrentVer(e.target.value)} placeholder="1.0.0" className={inputCls} />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Minimum Version</label>
            <input type="text" value={minVer} onChange={e => setMinVer(e.target.value)} placeholder="1.0.0" className={inputCls} />
          </div>
        </div>
        {version.platform !== 'web' && (
          <div className="mb-3">
            <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Store URL</label>
            <input type="url" value={storeUrl} onChange={e => setStoreUrl(e.target.value)} placeholder="https://apps.apple.com/..." className={inputCls} />
          </div>
        )}
        <div className="flex gap-2">
          <button type="button" onClick={() => setEditing(false)}
            className="flex-1 h-9 rounded-lg border border-stone text-[12px] text-slate font-semibold
                       hover:border-midnight hover:text-midnight transition-colors">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="flex-1 h-9 rounded-lg bg-plum text-white text-[12px] font-semibold
                       hover:bg-rose disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5">
            {saving ? <LoadingSpinner size="sm" /> : <><Check size={12} />Save</>}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-plum" />
          <p className="font-semibold text-small text-midnight">{PLATFORM_LABELS[version.platform]}</p>
        </div>
        <button type="button" onClick={startEdit}
          className="h-7 px-2 rounded-md text-[11px] font-semibold text-slate border border-stone
                     hover:border-plum hover:text-plum transition-colors flex items-center gap-1">
          <Pencil size={11} /> Edit
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[13px]">
        <div><span className="text-slate">Current:</span> <span className="font-semibold text-midnight">{version.currentVersion}</span></div>
        <div><span className="text-slate">Minimum:</span> <span className="font-semibold text-midnight">{version.minimumVersion}</span></div>
      </div>
      {version.storeUrl && (
        <p className="text-[11px] text-slate mt-1 truncate">{version.storeUrl}</p>
      )}
    </div>
  );
}

// ── Release Note Editor Modal ─────────────────────────────────────────────

function NoteEditorModal({
  note,
  onClose,
  onSaved,
}: {
  note: ReleaseNote | null; // null = new
  onClose: () => void;
  onSaved: () => void;
}) {
  const [version, setVersion] = useState(note?.version ?? '');
  const [platforms, setPlatforms] = useState<Platform[]>(note?.platforms ?? ['web', 'ios', 'android']);
  const [title, setTitle] = useState(note?.title ?? '');
  const [body, setBody] = useState(note?.body ?? '');
  const [published, setPublished] = useState(note?.published ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  function togglePlatform(p: Platform) {
    setPlatforms(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p],
    );
  }

  async function handleSave() {
    if (!version.trim()) { setError('Version is required'); return; }
    if (!title.trim()) { setError('Title is required'); return; }
    if (platforms.length === 0) { setError('Select at least one platform'); return; }

    setSaving(true);
    setError('');
    try {
      if (note) {
        await api.patch(`/admin/release-notes/${note.id}`, { version, platforms, title, body, published });
      } else {
        await api.post('/admin/release-notes', { version, platforms, title, body, published });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10
                     md:rounded-2xl md:max-w-lg md:w-full md:pb-6 max-h-[90vh] overflow-y-auto">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-h3 text-midnight">
            {note ? 'Edit Release Note' : 'New Release Note'}
          </h2>
          <button type="button" onClick={onClose}
            className="h-9 w-9 rounded-lg border border-stone text-slate flex items-center justify-center
                       hover:border-midnight hover:text-midnight transition-colors">
            <X size={16} />
          </button>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose/10 border border-rose/30">
            <AlertCircle size={14} className="shrink-0 text-rose mt-0.5" />
            <p className="text-[12px] text-rose">{error}</p>
          </div>
        )}

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Version</label>
              <input type="text" value={version} onChange={e => setVersion(e.target.value)} placeholder="1.3.0" className={inputCls} />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Status</label>
              <div className="flex items-center gap-3 h-10">
                <label className="flex items-center gap-2 text-[12px] font-medium text-midnight cursor-pointer">
                  <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)}
                    className="w-4 h-4 rounded border-stone text-plum focus:ring-plum/30" />
                  Published
                </label>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Platforms</label>
            <div className="flex gap-2">
              {(['web', 'ios', 'android'] as Platform[]).map(p => (
                <button key={p} type="button" onClick={() => togglePlatform(p)}
                  className={`h-8 px-3 rounded-lg text-[12px] font-semibold border transition-colors ${
                    platforms.includes(p)
                      ? 'border-plum bg-plum/10 text-plum'
                      : 'border-stone text-slate hover:border-plum/40'
                  }`}>
                  {PLATFORM_LABELS[p]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Title</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              placeholder="Job Completion Dates & Bug Fixes" className={inputCls} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] font-bold uppercase tracking-widest text-slate">Body (Markdown)</label>
              <button type="button" onClick={() => setShowPreview(p => !p)}
                className="text-[11px] font-semibold text-plum hover:text-rose transition-colors flex items-center gap-1">
                {showPreview ? <><Pencil size={10} />Edit</> : <><Eye size={10} />Preview</>}
              </button>
            </div>
            {showPreview ? (
              <div className="w-full min-h-[120px] px-3 py-2 rounded-lg border border-stone bg-mist/50
                              text-small text-midnight whitespace-pre-wrap">
                {body || <span className="text-slate italic">No content</span>}
              </div>
            ) : (
              <textarea value={body} onChange={e => setBody(e.target.value)}
                placeholder="- Fixed PDF generation for quotes with special characters&#10;- Improved loading performance" rows={6}
                className="w-full px-3 py-2 rounded-lg border border-stone bg-white text-small text-midnight
                           placeholder:text-slate/60 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors resize-y font-mono text-[13px]" />
            )}
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 h-11 rounded-xl border border-stone text-small text-slate font-semibold
                         hover:border-midnight hover:text-midnight transition-colors">
              Cancel
            </button>
            <button type="button" onClick={handleSave} disabled={saving}
              className="flex-1 h-11 rounded-xl bg-plum text-white text-small font-semibold
                         hover:bg-rose disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
              {saving ? <LoadingSpinner size="sm" /> : <><Check size={16} />{note ? 'Update' : 'Create'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Terms & Conditions Version Card ──────────────────────────────────────

function TermsVersionCard({ version, onSaved }: { version: string; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(version);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function startEdit() {
    setDraft(version);
    setError('');
    setEditing(true);
  }

  async function handleSave() {
    if (!draft.trim()) { setError('Version string is required (e.g. "1.0" or "2025-04-03")'); return; }
    setSaving(true);
    setError('');
    try {
      await api.patch('/admin/app-config', { currentTermsVersion: draft.trim() });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
        Terms & Conditions
      </p>
      <div className="bg-white border border-stone rounded-xl shadow-brand-sm p-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-plum" />
            <p className="font-semibold text-small text-midnight">Current T&C Version</p>
          </div>
          {!editing && (
            <button type="button" onClick={startEdit}
              className="h-7 px-2 rounded-md text-[11px] font-semibold text-slate border border-stone
                         hover:border-plum hover:text-plum transition-colors flex items-center gap-1">
              <Pencil size={11} /> Edit
            </button>
          )}
        </div>

        {!editing ? (
          <>
            <p className="text-[13px] text-midnight font-semibold">
              {version || <span className="text-slate font-normal italic">Not set — users will not see a T&C gate</span>}
            </p>
            <p className="text-[11px] text-slate mt-1 leading-snug">
              Changing this version will require all users to re-accept the Terms & Conditions before using the app.
            </p>
          </>
        ) : (
          <>
            {error && (
              <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30">
                <AlertCircle size={14} className="shrink-0 text-rose mt-0.5" />
                <p className="text-[12px] text-rose">{error}</p>
              </div>
            )}
            <div className="mb-3">
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Version String
              </label>
              <input
                type="text"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                placeholder='e.g. "1.0" or "2025-04-03"'
                className={inputCls}
              />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setEditing(false)}
                className="flex-1 h-9 rounded-lg border border-stone text-[12px] text-slate font-semibold
                           hover:border-midnight hover:text-midnight transition-colors">
                Cancel
              </button>
              <button type="button" onClick={handleSave} disabled={saving}
                className="flex-1 h-9 rounded-lg bg-plum text-white text-[12px] font-semibold
                           hover:bg-rose disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5">
                {saving ? <LoadingSpinner size="sm" /> : <><Check size={12} />Save</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export function AppVersionsPage() {
  const navigate = useNavigate();
  const [versions, setVersions] = useState<PlatformVersion[]>([]);
  const [notes, setNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingNote, setEditingNote] = useState<ReleaseNote | null | undefined>(undefined);
  // undefined = closed, null = new, ReleaseNote = editing existing
  const [deleting, setDeleting] = useState<string | null>(null);
  const [termsVersion, setTermsVersion] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [vRes, nRes, configRes] = await Promise.all([
        api.get<{ success: boolean; data: PlatformVersion[] }>('/admin/app-versions'),
        api.get<{ success: boolean; data: ReleaseNote[] }>('/admin/release-notes'),
        api.get<{ success: boolean; data: { currentTermsVersion?: string } }>('/admin/app-config'),
      ]);
      setVersions(vRes.data);
      setNotes(nRes.data);
      setTermsVersion(configRes.data.currentTermsVersion ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function handleDelete(id: string) {
    setDeleting(id);
    try {
      await api.del(`/admin/release-notes/${id}`);
      await fetchAll();
    } catch { /* ignore */ }
    setDeleting(null);
  }

  return (
    <div className="px-4 pt-6 pb-12 md:px-8 md:max-w-2xl md:mx-auto">
      <button
        type="button"
        onClick={() => navigate('/admin/platform-tools')}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        Platform Admin Tools
      </button>

      <h1 className="font-display text-h1 text-midnight mb-1">App Versions & Release Notes</h1>
      <p className="text-[13px] text-slate mb-6 leading-snug">
        Manage platform versions and publish release notes for users.
      </p>

      {loading && (
        <div className="flex justify-center py-16"><LoadingSpinner size="lg" /></div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="flex flex-col gap-6">
          {/* ── Terms & Conditions Version ────────────────────────── */}
          <TermsVersionCard version={termsVersion} onSaved={fetchAll} />

          {/* ── Platform Versions ──────────────────────────────────── */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
              Platform Versions
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {versions.map(v => (
                <PlatformCard key={v.platform} version={v} onSaved={fetchAll} />
              ))}
            </div>
          </div>

          {/* ── Release Notes ──────────────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate">
                Release Notes
              </p>
              <button type="button" onClick={() => setEditingNote(null)}
                className="h-8 px-3 rounded-lg bg-plum text-white text-[12px] font-semibold
                           hover:bg-rose transition-colors flex items-center gap-1.5">
                <Plus size={13} /> New Note
              </button>
            </div>

            {notes.length === 0 ? (
              <div className="bg-white border border-stone rounded-xl shadow-brand-sm px-4 py-8 text-center">
                <p className="text-[13px] text-slate">No release notes yet. Create one to get started.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {notes.map(note => (
                  <div key={note.id} className="bg-white border border-stone rounded-xl shadow-brand-sm px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[12px] font-mono font-semibold text-plum">v{note.version}</span>
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                            note.published ? 'bg-emerald-50 text-emerald-700' : 'bg-stone text-slate'
                          }`}>
                            {note.published ? 'Published' : 'Draft'}
                          </span>
                          <div className="flex gap-1">
                            {note.platforms.map(p => (
                              <span key={p} className="text-[10px] font-semibold text-slate bg-mist px-1.5 py-0.5 rounded">
                                {PLATFORM_LABELS[p]}
                              </span>
                            ))}
                          </div>
                        </div>
                        <p className="text-[13px] font-semibold text-midnight truncate">{note.title}</p>
                        <p className="text-[11px] text-slate">
                          {new Date(note.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button type="button" onClick={() => setEditingNote(note)}
                          className="h-8 w-8 rounded-lg text-slate hover:text-plum hover:bg-plum/10
                                     flex items-center justify-center transition-colors">
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => handleDelete(note.id)}
                          disabled={deleting === note.id}
                          className="h-8 w-8 rounded-lg text-slate hover:text-rose hover:bg-rose/10
                                     flex items-center justify-center transition-colors disabled:opacity-50">
                          {deleting === note.id ? <LoadingSpinner size="sm" /> : <Trash2 size={14} />}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Note editor modal */}
      {editingNote !== undefined && (
        <NoteEditorModal
          note={editingNote}
          onClose={() => setEditingNote(undefined)}
          onSaved={fetchAll}
        />
      )}
    </div>
  );
}
